import { Button } from "@/components/ui/button";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import type { GradingDimension } from "@/types/entryConfig";
import type {
  GradingBucketDraft,
  GradingValidationResult,
} from "@/utils/gradingUtils";
import {
  DRAG_STEP_CM,
  DRAG_STEP_IN,
  axisMaxCm,
  clampCut,
  cmToInchFraction,
  cmToPct,
  cmToUnit,
  colorForGradeIndex,
  draftsToLineModel,
  formatUnit,
  lineModelToDrafts,
  pxToCm,
  removeGrade as removeGradeFromModel,
  roundCm,
  SEED_CUT_CM,
  seedLineModel,
  snapDisplayCm,
  splitGradeAt,
  unitToCm,
  type GradingDisplayUnit,
  type GradingLineModel,
} from "@/utils/gradingLineModel";
import { ChevronRight, Plus, RotateCcw, X } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

export interface ImageMeasurementGradingSectionProps {
  dimension: GradingDimension;
  drafts: GradingBucketDraft[];
  validation: GradingValidationResult;
  isOpen: boolean;
  isSystemTrait?: boolean;
  onDimensionChange: (dimension: GradingDimension) => void;
  onDraftsChange: (drafts: GradingBucketDraft[]) => void;
  onOpenChange: (open: boolean) => void;
  /**
   * Reports whether the editor's typed thresholds currently form a valid
   * (strictly increasing) set. Typed-but-out-of-order values are held locally
   * and NOT emitted via onDraftsChange, so the parent uses this to block save
   * (and avoid silently dropping what the user typed) until the order is fixed.
   */
  onValidityChange?: (valid: boolean) => void;
  /**
   * Reports whether grading is "active" — i.e. the user has actually configured it,
   * vs. the untouched default seed (Grade 1 / Grade 2 at the default cut). The parent
   * only attaches `grading` to the saved trait when active, so merely opening the
   * (optional) section and saving doesn't silently attach default grades. Mirrors the
   * image-capture-requirements pattern: dulled + excluded until the user changes something.
   */
  onActiveChange?: (active: boolean) => void;
}

const newBucketId = (): string =>
  typeof crypto !== "undefined" && "randomUUID" in crypto
    ? (crypto as Crypto).randomUUID()
    : `b-${Math.random().toString(36).slice(2, 10)}-${Date.now()}`;

/**
 * A cut set is committable only when it's strictly increasing, every value is a
 * finite number, and the first cut is positive. NaN (unparseable/empty draft)
 * fails. Used both to gate a typed commit and to derive live ordering flags.
 */
const cutsAreOrdered = (cm: number[]): boolean =>
  cm.every((v, i) => Number.isFinite(v) && (i === 0 ? v > 0 : v > cm[i - 1]));

interface GradeInterval {
  index: number;
  id: string;
  label: string;
  lo: number;
  hi: number; // Infinity for the unbounded top grade
  kind: "lessThan" | "between" | "greaterThanOrEqual";
}

const buildIntervals = (model: GradingLineModel): GradeInterval[] => {
  const { grades, cuts } = model;
  const n = grades.length;
  return grades.map((g, i) => ({
    index: i,
    id: g.id,
    label: g.label,
    lo: i === 0 ? 0 : cuts[i - 1],
    hi: i === n - 1 ? Infinity : cuts[i],
    kind: i === 0 ? "lessThan" : i === n - 1 ? "greaterThanOrEqual" : "between",
  }));
};

// closed (inclusive) and open (exclusive) endpoint glyphs, matching the bar markers
const ClosedDot = () => (
  <span className="inline-block h-[9px] w-[9px] rounded-full bg-foreground align-middle" aria-hidden />
);
const OpenDot = () => (
  <span className="inline-block h-[9px] w-[9px] rounded-full border-[1.5px] border-foreground bg-background align-middle" aria-hidden />
);

/**
 * Range text for a grade with open/closed endpoint glyphs.
 * `compact` (used for the on-bar chips) drops the "(no upper limit)" tail, which
 * the ruler already states, so the top-grade chip doesn't collide with it.
 */
function IntervalText({
  iv,
  dimension,
  unit,
  fractions = false,
  compact = false,
}: {
  iv: GradeInterval;
  dimension: string;
  unit: GradingDisplayUnit;
  /** Render bounds as canonical inch fractions (e.g. `1 1/16"`) rather than a
   *  decimal value plus a unit word. Used for read-only (system) traits. */
  fractions?: boolean;
  compact?: boolean;
}) {
  // A bound is either a canonical inch fraction (which carries its own `"` mark)
  // or a decimal value followed by the unit word.
  const bound = (cm: number) => (fractions ? cmToInchFraction(cm) : `${formatUnit(cm, unit)} ${unit}`);
  if (iv.kind === "greaterThanOrEqual") {
    return (
      <span className="inline-flex items-center gap-1.5">
        <ClosedDot /> {dimension} ≥ {bound(iv.lo)}
        {compact ? null : <>&nbsp;(no upper limit)</>}
      </span>
    );
  }
  const lo = iv.kind === "lessThan" ? (fractions ? '0"' : "0") : bound(iv.lo);
  return (
    <span className="inline-flex items-center gap-1.5">
      <ClosedDot /> {lo} ≤ {dimension} &lt; {bound(iv.hi)} <OpenDot />
    </span>
  );
}

export function ImageMeasurementGradingSection({
  dimension,
  drafts,
  validation,
  isOpen,
  isSystemTrait,
  onDimensionChange,
  onDraftsChange,
  onOpenChange,
  onValidityChange,
  onActiveChange,
}: ImageMeasurementGradingSectionProps) {
  const disabled = !!isSystemTrait;
  const seededForOpenRef = useRef(false);
  const onValidityChangeRef = useRef(onValidityChange);
  onValidityChangeRef.current = onValidityChange;
  const onActiveChangeRef = useRef(onActiveChange);
  onActiveChangeRef.current = onActiveChange;

  const model = useMemo(() => draftsToLineModel(drafts), [drafts]);
  const { grades, cuts } = model;
  const count = grades.length;
  const axisMax = useMemo(() => axisMaxCm(cuts), [cuts]);
  const intervals = useMemo(() => buildIntervals(model), [model]);

  const modelRef = useRef(model);
  modelRef.current = model;
  const onDraftsChangeRef = useRef(onDraftsChange);
  onDraftsChangeRef.current = onDraftsChange;

  const trackRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<{ index: number; pointerId: number; axisMax: number } | null>(null);

  // in-progress inline edits: names keyed by grade id, cut values keyed by cut index
  const [nameDraft, setNameDraft] = useState<Record<string, string>>({});
  const [cutDraft, setCutDraft] = useState<Record<number, string>>({});
  // Display/entry unit — purely for the editing session; storage stays cm→meters.
  // Defaults to inches: growers/processors read produce size grades in inches.
  const [unit, setUnit] = useState<GradingDisplayUnit>("in");
  const unitRef = useRef(unit);
  unitRef.current = unit;
  // Read-only (system) traits in inch mode render their bounds as canonical
  // fractions (3/4", 1 1/16", …) instead of editable decimals. Editable traits
  // keep decimal inputs so values stay typeable; the inch toggle still applies.
  const inchFractions = disabled && unit === "in";

  // Effective cuts = committed cuts with in-progress typed drafts applied (cm).
  // A draft entry is NaN when empty/unparseable. Typing never clamps — out-of-order
  // values live here and surface as flags until the whole set is ordered again.
  const computePending = useCallback(
    (draftMap: Record<number, string>, baseCuts: number[]): number[] =>
      baseCuts.map((c, i) => {
        const raw = draftMap[i];
        if (raw === undefined) return c;
        const t = raw.trim();
        if (t === "") return NaN;
        const n = Number(t);
        return Number.isFinite(n) ? unitToCm(n, unit) : NaN;
      }),
    [unit]
  );

  // Per-cut ordering errors + overall validity, recomputed live so flags appear
  // and clear as the user types (commit itself happens on blur in commitCut).
  const { cutErrors, pendingValid } = useMemo(() => {
    const pending = computePending(cutDraft, cuts);
    const errors = pending.map((v, i) => {
      if (!Number.isFinite(v)) return "Enter a number";
      const prev = i > 0 ? pending[i - 1] : null;
      const next = i < pending.length - 1 ? pending[i + 1] : null;
      if (i === 0 && v <= 0) return "Must be greater than 0";
      if (prev !== null && Number.isFinite(prev) && v <= prev)
        return `Must be above ${formatUnit(prev, unit)} ${unit}`;
      if (next !== null && Number.isFinite(next) && v >= next)
        return `Must be below ${formatUnit(next, unit)} ${unit}`;
      return null;
    });
    return { cutErrors: errors, pendingValid: errors.every((e) => e === null) };
  }, [computePending, cutDraft, cuts, unit]);

  // Tell the parent whether the typed thresholds are currently committable, so it
  // can block save (rather than silently dropping out-of-order values on submit).
  useEffect(() => {
    onValidityChangeRef.current?.(pendingValid);
  }, [pendingValid]);

  // Seed two blank grades the first time the section is opened empty.
  useEffect(() => {
    if (!isOpen) {
      seededForOpenRef.current = false;
      return;
    }
    if (seededForOpenRef.current) return;
    seededForOpenRef.current = true;
    if (drafts.length === 0) {
      onDraftsChange(lineModelToDrafts(seedLineModel(newBucketId)));
    }
  }, [drafts.length, isOpen, onDraftsChange]);

  const commitModel = useCallback(
    (next: GradingLineModel) => onDraftsChangeRef.current(lineModelToDrafts(next)),
    []
  );

  const setCutValue = useCallback(
    (index: number, valueCm: number) => {
      const m = modelRef.current;
      const clamped = clampCut(m.cuts, index, valueCm);
      commitModel({
        grades: m.grades,
        cuts: m.cuts.map((c, i) => (i === index ? clamped : c)),
      });
    },
    [commitModel]
  );

  const renameGrade = (index: number, label: string) => {
    const v = label.trim().slice(0, 32);
    if (!v) return; // revert: a grade always keeps a name (default "Grade N")
    const m = modelRef.current;
    commitModel({
      grades: m.grades.map((g, i) => (i === index ? { ...g, label: v } : g)),
      cuts: m.cuts,
    });
  };

  const clearNameDraft = (id: string) =>
    setNameDraft((d) => {
      const next = { ...d };
      delete next[id];
      return next;
    });

  // --- pointer drag on the stem (axisMax frozen at drag start so the unbounded
  // top cut tracks the pointer instead of feeding its own growth back in) ---
  const handlePointerMove = useCallback(
    (event: PointerEvent) => {
      const drag = dragRef.current;
      const track = trackRef.current;
      if (!drag || !track) return;
      const rect = track.getBoundingClientRect();
      const cm = pxToCm(event.clientX, rect.left, rect.width, drag.axisMax);
      setCutValue(drag.index, snapDisplayCm(cm, unitRef.current));
    },
    [setCutValue]
  );
  const handlePointerUp = useCallback(() => {
    dragRef.current = null;
    window.removeEventListener("pointermove", handlePointerMove);
    window.removeEventListener("pointerup", handlePointerUp);
  }, [handlePointerMove]);
  useEffect(
    () => () => {
      window.removeEventListener("pointermove", handlePointerMove);
      window.removeEventListener("pointerup", handlePointerUp);
    },
    [handlePointerMove, handlePointerUp]
  );
  const startDrag = (index: number) => (event: React.PointerEvent<HTMLElement>) => {
    if (disabled || event.button !== 0) return;
    // clicking the value pill (or its edit input) should edit, not drag
    if ((event.target as HTMLElement).closest("[data-cut-edit]")) return;
    event.preventDefault();
    dragRef.current = { index, pointerId: event.pointerId, axisMax: axisMaxCm(modelRef.current.cuts) };
    event.currentTarget.setPointerCapture?.(event.pointerId);
    window.addEventListener("pointermove", handlePointerMove);
    window.addEventListener("pointerup", handlePointerUp);
  };

  // Keyboard nudges in the displayed unit (1mm in cm, 0.05in in inches).
  const onHandleKeyDown = (index: number) => (event: React.KeyboardEvent<HTMLElement>) => {
    if (disabled) return;
    const cur = modelRef.current.cuts[index];
    const step = unit === "in" ? DRAG_STEP_IN : DRAG_STEP_CM;
    const curDisp = cmToUnit(cur, unit);
    let nextDisp: number | null = null;
    if (event.key === "ArrowRight" || event.key === "ArrowUp") nextDisp = curDisp + step;
    else if (event.key === "ArrowLeft" || event.key === "ArrowDown") nextDisp = curDisp - step;
    else if (event.key === "PageUp") nextDisp = curDisp + 0.5;
    else if (event.key === "PageDown") nextDisp = curDisp - 0.5;
    if (nextDisp !== null) {
      event.preventDefault();
      setCutValue(index, unitToCm(nextDisp, unit));
    }
  };

  const clearCutDraft = (index: number) =>
    setCutDraft((d) => {
      const next = { ...d };
      delete next[index];
      return next;
    });

  // Free typed entry: never clamps. On blur we evaluate the WHOLE set (committed
  // cuts with every in-progress draft applied). If it's strictly increasing, commit
  // it and clear the drafts; otherwise keep the typed values shown + flagged so the
  // user can fix the order without losing what they entered (e.g. enter 8 grades top
  // to bottom). Dragging/keyboard still clamp via setCutValue.
  const commitCut = (index: number, raw: string) => {
    const nextDrafts = { ...cutDraft, [index]: raw };
    const m = modelRef.current;
    const pending = computePending(nextDrafts, m.cuts);
    if (cutsAreOrdered(pending)) {
      // Only emit when a *displayed* value actually changed — committing an
      // unchanged inch value (1.00cm shown as 0.39in) would re-parse to 0.99cm
      // and drift the stored value through display rounding.
      const changed = pending.some((v, i) => formatUnit(v, unit) !== formatUnit(m.cuts[i], unit));
      if (changed) commitModel({ grades: m.grades, cuts: pending });
      setCutDraft({});
    } else {
      setCutDraft(nextDrafts);
    }
  };

  // Shared props for any field that edits cut[k] — the ruler pill AND the range
  // endpoints in the table. Because a cut is one shared value (grade k's upper edge
  // and grade k+1's lower edge), every field bound to the same k stays in sync, so
  // they all show the same draft and the same out-of-order flag (aria-invalid).
  const cutFieldProps = (k: number) => ({
    "data-cut-edit": true,
    type: "text" as const,
    inputMode: "decimal" as const,
    disabled,
    "aria-invalid": cutErrors[k] ? true : undefined,
    title: cutErrors[k] ?? undefined,
    value: k in cutDraft ? cutDraft[k] : inchFractions ? cmToInchFraction(cuts[k]) : formatUnit(cuts[k], unit),
    // Seed the draft from the committed value on focus, but never clobber an existing
    // draft — refocusing a flagged out-of-order value must keep what the user typed.
    onFocus: () => setCutDraft((d) => (k in d ? d : { ...d, [k]: formatUnit(cuts[k], unit) })),
    onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
      setCutDraft((d) => ({ ...d, [k]: e.target.value })),
    onBlur: (e: React.FocusEvent<HTMLInputElement>) => commitCut(k, e.currentTarget.value),
    onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter") {
        e.preventDefault();
        e.currentTarget.blur();
      } else if (e.key === "Escape") {
        e.stopPropagation();
        clearCutDraft(k);
        e.currentTarget.blur();
      }
    },
  });

  const tableCutClass =
    "inline-block h-6 w-16 rounded border border-input bg-background px-1 text-center font-mono text-[13px] text-foreground focus:border-primary focus:outline-none disabled:cursor-default disabled:opacity-70 aria-[invalid=true]:border-destructive aria-[invalid=true]:text-destructive aria-[invalid=true]:focus:border-destructive";

  // A resolved-table range endpoint. Read-only (system) traits in inch mode show
  // the bound as a canonical fraction (no editable box, no decimals); editable
  // traits get the shared cut Input so the value stays typeable.
  const renderCutField = (cutIndex: number, testid: string, ariaLabel: string) =>
    inchFractions ? (
      <span data-testid={testid} className="font-mono text-[13px] text-foreground">
        {cmToInchFraction(cuts[cutIndex])}
      </span>
    ) : (
      <Input
        data-testid={testid}
        aria-label={ariaLabel}
        {...cutFieldProps(cutIndex)}
        className={tableCutClass}
      />
    );

  const gradeName = (i: number) => grades[i]?.label.trim() || `Grade ${i + 1}`;
  const splitAt = (i: number) => commitModel(splitGradeAt(modelRef.current, i, newBucketId));
  const remove = (i: number) => commitModel(removeGradeFromModel(modelRef.current, i));
  const addGrade = () => splitAt(count - 1); // split the unbounded top grade
  const seedFresh = () => commitModel(seedLineModel(newBucketId));
  // Reset returns to the dulled, inactive default seed (so grading is excluded from
  // save again) — the grading analogue of the capture sliders' "Reset to default".
  const resetToDefault = () => {
    setCutDraft({});
    commitModel(seedLineModel(newBucketId));
  };

  // Collapsing while the editor is still the untouched default seed clears it, so
  // merely opening the optional section out of curiosity and closing it again does
  // not attach a grading config the user never actually set up.
  const isPristineSeed =
    count === 2 &&
    grades[0]?.label === "Grade 1" &&
    grades[1]?.label === "Grade 2" &&
    cuts.length === 1 &&
    cuts[0] === SEED_CUT_CM;

  // "Active" = the user has actually configured grading (renamed a grade, moved/typed a
  // boundary, added a grade), vs. the untouched default seed. Dulled + excluded from save
  // until active; reported up so the parent only persists grading when it's real.
  const active = count >= 2 && !isPristineSeed;
  useEffect(() => {
    onActiveChangeRef.current?.(active);
  }, [active]);
  const handleOpenChange = (open: boolean) => {
    if (!open) {
      // Discard unresolved typed thresholds on collapse: a half-typed out-of-order value
      // would otherwise keep reporting invalid (onValidityChange(false)) and leave the
      // parent's save permanently blocked. Clearing resets validity to true.
      setCutDraft({});
      if (isPristineSeed) onDraftsChange([]);
    }
    onOpenChange(open);
  };

  // ruler ticks in the displayed unit: every 0.5 (minor), labelled at integers
  // (major). `cm` is the position (geometry stays cm); `label` is the display value.
  const ticks = useMemo(() => {
    const maxDisp = cmToUnit(axisMax, unit);
    const out: { cm: number; label: number; major: boolean }[] = [];
    for (let v = 0; v <= maxDisp + 1e-6; v = Math.round((v + 0.5) * 100) / 100) {
      out.push({
        cm: unitToCm(v, unit),
        label: Math.round(v),
        major: Math.abs(v - Math.round(v)) < 1e-6,
      });
    }
    return out;
  }, [axisMax, unit]);

  return (
    <Collapsible
      open={isOpen}
      onOpenChange={handleOpenChange}
      data-testid="image-measurement-grading-section"
      className="border-t border-border pt-4"
    >
      <CollapsibleTrigger asChild>
        <button type="button" className="flex w-full items-center gap-2 text-sm font-medium">
          <ChevronRight className={`h-4 w-4 transition-transform ${isOpen ? "rotate-90" : ""}`} />
          <span>Add Size Grades (optional)</span>
        </button>
      </CollapsibleTrigger>

      <CollapsibleContent className="mt-3 space-y-4">
        <p className="text-sm text-muted-foreground">
          Slide the cut points to set where one grade ends and the next begins. Because grades share a
          single cut, they always tile the whole measurement range — no gaps, no overlaps, ever.
        </p>

        {/* control bar */}
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2">
            <span className="font-mono text-[10.5px] uppercase tracking-[0.16em] text-muted-foreground">
              Grading by
            </span>
            <ToggleGroup
              type="single"
              value={dimension}
              disabled={isSystemTrait}
              onValueChange={(v) => v && onDimensionChange(v as GradingDimension)}
              aria-label="Applying Grading Based On"
            >
              <ToggleGroupItem value="length">Length</ToggleGroupItem>
              <ToggleGroupItem value="width">Width</ToggleGroupItem>
            </ToggleGroup>
          </div>
          {count > 0 && (
            <Button type="button" variant="outline" size="sm" disabled={disabled} onClick={addGrade}>
              <Plus className="mr-1 h-4 w-4" /> Add grade
            </Button>
          )}
          {count > 0 && (
            <div className="flex items-center gap-2">
              <span className="font-mono text-[10.5px] uppercase tracking-[0.16em] text-muted-foreground">
                Units
              </span>
              <ToggleGroup
                type="single"
                value={unit}
                onValueChange={(v) => {
                  if (!v) return;
                  setCutDraft({}); // drop half-typed values so units never cross
                  setUnit(v as GradingDisplayUnit);
                }}
                aria-label="Display and entry units"
              >
                <ToggleGroupItem value="cm" aria-label="Centimeters">cm</ToggleGroupItem>
                <ToggleGroupItem value="in" aria-label="Inches">in</ToggleGroupItem>
              </ToggleGroup>
            </div>
          )}
        </div>

        {count === 0 ? (
          <div className="rounded-md border border-dashed p-4 text-sm text-muted-foreground">
            <p className="mb-3">No size grades. Add grades to bucket measurements.</p>
            <Button type="button" size="sm" disabled={disabled} onClick={seedFresh}>
              <Plus className="mr-1 h-4 w-4" /> Add size grades
            </Button>
          </div>
        ) : (
          <TooltipProvider delayDuration={0}>
            {!active && (
              <p className="text-xs text-muted-foreground">
                These default grades aren&apos;t saved yet — rename a grade, drag a boundary, or
                add a grade to turn grading on. Reset returns to this off state.
              </p>
            )}
            {/* Dulled (opacity-70) until the user configures grading — mirrors the
                capture-requirement "suggested" state; transitions to full opacity ("comes
                to life") on the first real change. */}
            <div
              data-testid="grading-instrument"
              data-active={active}
              className={cn("space-y-4 transition-opacity", !active && "opacity-70")}
            >
            {/* the instrument */}
            <div className="rounded-md border border-border bg-muted/30 px-6 pb-3 pt-7">
              {/* track-frame: room above for labels */}
              <div className="relative pt-11">
                {/* bar */}
                <div
                  ref={trackRef}
                  data-testid="grading-line-track"
                  className="relative h-[62px] w-full select-none"
                >
                  {intervals.map((iv) => {
                    const isTop = iv.kind === "greaterThanOrEqual";
                    const leftPct = cmToPct(iv.lo, axisMax);
                    const hiPct = isTop ? 100 : cmToPct(iv.hi, axisMax);
                    // Only show the on-bar range chip when the segment is wide enough
                    // to fit it; on thin grades it just truncates to noise — the pills
                    // and the resolved-grades table below carry the values there.
                    const showChip = hiPct - leftPct >= 14;
                    return (
                      <div
                        key={iv.id}
                        data-testid={`grading-segment-${iv.index}`}
                        className="group absolute inset-y-0 flex items-center justify-center border-y-[1.5px] border-foreground/50"
                        style={{
                          left: `${leftPct}%`,
                          width: `${hiPct - leftPct}%`,
                          backgroundColor: colorForGradeIndex(iv.index, count),
                          ...(iv.index === 0 ? { borderLeft: "1.5px solid hsl(var(--foreground) / 0.6)" } : {}),
                        }}
                      >
                        {/* editable name, floating above the bar */}
                        <div className="pointer-events-none absolute bottom-[calc(100%+8px)] left-0 right-0 flex justify-center">
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <input
                                data-testid={`grading-name-${iv.index}`}
                                aria-label={`Grade ${iv.index + 1} name`}
                                disabled={disabled}
                                placeholder="Name"
                                value={iv.id in nameDraft ? nameDraft[iv.id] : iv.label}
                                maxLength={32}
                                onFocus={() => setNameDraft((d) => ({ ...d, [iv.id]: iv.label }))}
                                onChange={(e) => setNameDraft((d) => ({ ...d, [iv.id]: e.target.value }))}
                                onBlur={(e) => {
                                  renameGrade(iv.index, e.target.value);
                                  clearNameDraft(iv.id);
                                }}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter") (e.target as HTMLInputElement).blur();
                                  else if (e.key === "Escape") {
                                    e.stopPropagation();
                                    clearNameDraft(iv.id);
                                    (e.target as HTMLInputElement).blur();
                                  }
                                }}
                                className="pointer-events-auto max-w-full truncate rounded border border-transparent bg-background px-2 py-0.5 text-center text-sm font-medium text-foreground hover:border-border focus:border-primary focus:outline-none"
                                style={{
                                  // content width + room for padding so a short placeholder
                                  // like "Name" isn't clipped to "Na…"
                                  width: `calc(${Math.min(
                                    Math.max((iv.label || "Name").length, 4),
                                    20
                                  )}ch + 1.6rem)`,
                                }}
                              />
                            </TooltipTrigger>
                            {iv.label.trim() && <TooltipContent>{iv.label}</TooltipContent>}
                          </Tooltip>
                        </div>

                        {/* resolved range chip, below the bar (only when it fits) */}
                        {showChip && (
                          <div className="absolute left-0 right-0 top-[calc(100%+7px)] truncate px-0.5 text-center font-mono text-[10.5px] text-muted-foreground">
                            <IntervalText iv={iv} dimension={dimension} unit={unit} fractions={inchFractions} compact />
                          </div>
                        )}

                        {/* top segment fades into infinity */}
                        {isTop && (
                          <div
                            className="pointer-events-none absolute -bottom-[1.5px] -top-[1.5px] right-[-1px] w-[46%]"
                            style={{ background: "linear-gradient(to right, transparent, hsl(var(--muted)) 86%)" }}
                            aria-hidden
                          />
                        )}

                        {/* remove (×), top-right on hover */}
                        <button
                          type="button"
                          aria-label={`Remove grade ${gradeName(iv.index)}`}
                          disabled={disabled || count <= 2}
                          onClick={() => remove(iv.index)}
                          className={cn(
                            "absolute right-1 top-1 flex h-[18px] w-[18px] items-center justify-center rounded-full border border-foreground/30 bg-background/80 font-mono text-[11px] leading-none text-muted-foreground opacity-0 transition hover:border-destructive hover:bg-destructive hover:text-destructive-foreground focus-visible:opacity-100 group-hover:opacity-100",
                            (disabled || count <= 2) && "hidden"
                          )}
                        >
                          ×
                        </button>

                        {/* split (+), bottom-center on hover */}
                        <button
                          type="button"
                          data-testid={`grading-split-${iv.index}`}
                          aria-label={`Split grade ${gradeName(iv.index)}`}
                          disabled={disabled}
                          onClick={() => splitAt(iv.index)}
                          className="absolute bottom-1 left-1/2 flex h-5 w-5 -translate-x-1/2 items-center justify-center rounded-full border border-foreground/30 bg-background/80 font-mono text-[14px] leading-none text-muted-foreground opacity-0 transition hover:bg-foreground hover:text-background focus-visible:opacity-100 group-hover:opacity-100 disabled:hidden"
                        >
                          +
                        </button>
                      </div>
                    );
                  })}

                  {/* zero marker (closed/inclusive at 0) */}
                  <span
                    className="absolute top-1/2 z-[4] h-3 w-3 -translate-x-1/2 -translate-y-1/2 rounded-full border-[1.5px] border-foreground bg-foreground"
                    style={{ left: "0%" }}
                    aria-hidden
                  />

                  {/* per-cut: filled boundary dot + draggable stem/pill handle */}
                  {cuts.map((cut, i) => {
                    const leftPct = cmToPct(cut, axisMax);
                    const min = i === 0 ? 0 : cuts[i - 1];
                    const max = i === cuts.length - 1 ? axisMax : cuts[i + 1];
                    return (
                      <div key={`cut-${i}`}>
                        {/* boundary dot on the bar (value belongs to the grade above) */}
                        <span
                          className="absolute top-1/2 z-[4] h-[11px] w-[11px] -translate-x-1/2 -translate-y-1/2 rounded-full border-[1.5px] border-foreground bg-foreground"
                          style={{ left: `${leftPct}%` }}
                          aria-hidden
                        />
                        {/* handle: accent stem + value pill below the bar */}
                        <div
                          data-testid={`grading-handle-${i}`}
                          role="slider"
                          tabIndex={disabled ? -1 : 0}
                          aria-label={`Cut point ${i + 1} between ${gradeName(i)} and ${gradeName(i + 1)}`}
                          aria-orientation="horizontal"
                          aria-valuemin={cmToUnit(roundCm(min), unit)}
                          aria-valuemax={cmToUnit(roundCm(max), unit)}
                          aria-valuenow={cmToUnit(cut, unit)}
                          aria-valuetext={`${formatUnit(cut, unit)} ${unit}`}
                          onKeyDown={onHandleKeyDown(i)}
                          onPointerDown={startDrag(i)}
                          className="absolute top-0 z-[6] flex -translate-x-1/2 flex-col items-center focus:outline-none"
                          style={{ left: `${leftPct}%` }}
                        >
                          {/* stem = drag grip; spans the bar down to the ruler line (90px) */}
                          <div
                            className={cn(
                              "h-[90px] w-[2px] bg-primary",
                              disabled ? "cursor-not-allowed opacity-60" : "cursor-ew-resize"
                            )}
                          />
                          {/* value pill — an always-editable field (drag is the stem above) */}
                          <Input
                            data-testid={`grading-cut-${i}`}
                            aria-label={`Boundary between ${gradeName(i)} and ${gradeName(i + 1)} in ${unit === "in" ? "inches" : "centimeters"}`}
                            {...cutFieldProps(i)}
                            className="absolute top-[94px] h-auto w-16 cursor-text rounded-full border-[1.5px] border-primary bg-background px-1 py-[3px] text-center font-mono text-xs font-semibold text-primary focus:bg-background aria-[invalid=true]:border-destructive aria-[invalid=true]:text-destructive"
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* ruler axis. Tick number labels sit below the cut pills (which hang
                    just under the ruler line); the rightmost label is suppressed so it
                    doesn't collide with the "cm" unit. */}
                <div className="relative mt-7 h-12 border-t-[1.5px] border-foreground" aria-hidden>
                  {ticks.map((t) => (
                    <span
                      key={t.cm}
                      className={cn(
                        "absolute top-0 w-px -translate-x-1/2",
                        t.major ? "h-[11px] bg-muted-foreground" : "h-[6px] bg-border"
                      )}
                      style={{ left: `${cmToPct(t.cm, axisMax)}%` }}
                    />
                  ))}
                  {ticks
                    .filter((t) => t.major && cmToPct(t.cm, axisMax) <= 92)
                    .map((t) => (
                      <span
                        key={`l-${t.cm}`}
                        className="absolute top-[28px] -translate-x-1/2 font-mono text-[10.5px] text-muted-foreground"
                        style={{ left: `${cmToPct(t.cm, axisMax)}%` }}
                      >
                        {t.label}
                      </span>
                    ))}
                  <span className="absolute right-0 top-[28px] font-mono text-[10.5px] tracking-[0.1em] text-muted-foreground">
                    {unit}
                  </span>
                </div>
              </div>
            </div>

            {/* validity readout — overlap/coverage are structurally impossible here,
                so this only ever shows for a malformed hydration; no green "covered"
                reassurance box (it's noise). */}
            {(validation.overlapError || validation.coverageError) && (
              <div className="flex items-start gap-3 rounded-md border border-destructive bg-destructive/5 px-4 py-3">
                <span className="mt-1 h-[11px] w-[11px] flex-none rounded-full bg-destructive" aria-hidden />
                <span className="text-sm text-foreground">
                  {validation.overlapError || validation.coverageError}
                </span>
              </div>
            )}
            {count > 0 && count < 2 && (
              <p className="text-sm text-muted-foreground">Add at least two grades.</p>
            )}
            {/* Typed thresholds are out of order — the offending fields are flagged
                (aria-invalid) and nothing commits until the set increases again. */}
            {!pendingValid && (
              <p className="text-sm text-destructive">
                Thresholds must increase down the list — fix the highlighted value{count > 2 ? "s" : ""}.
              </p>
            )}

            {/* per-grade name errors (empty/duplicate) */}
            {grades.map((g, i) =>
              validation.bucketErrors[g.id] ? (
                <p key={`err-${g.id}`} className="text-sm text-destructive">
                  {gradeName(i)}: {validation.bucketErrors[g.id]}
                </p>
              ) : null
            )}

            {/* resolved grades table */}
            <div data-testid="grading-resolved" className="overflow-hidden rounded-md border border-border">
              <h4 className="border-b border-border bg-muted/40 px-4 py-3 font-mono text-[10.5px] uppercase tracking-[0.18em] text-muted-foreground">
                Resolved grades · by {dimension}
              </h4>
              {intervals.map((iv) => (
                <div
                  key={iv.id}
                  className="grid grid-cols-[14px_1fr_auto] items-center gap-3 border-b border-border px-4 py-3 last:border-b-0"
                >
                  <span
                    className="h-[14px] w-[14px] rounded-sm border border-foreground/25"
                    style={{ backgroundColor: colorForGradeIndex(iv.index, count) }}
                    aria-hidden
                  />
                  {/* editable grade name (also editable on the bar) */}
                  <input
                    data-testid={`grading-table-name-${iv.index}`}
                    aria-label={`Grade ${iv.index + 1} name`}
                    disabled={disabled}
                    value={iv.id in nameDraft ? nameDraft[iv.id] : iv.label}
                    maxLength={32}
                    onFocus={() => setNameDraft((d) => ({ ...d, [iv.id]: iv.label }))}
                    onChange={(e) => setNameDraft((d) => ({ ...d, [iv.id]: e.target.value }))}
                    onBlur={(e) => {
                      renameGrade(iv.index, e.target.value);
                      clearNameDraft(iv.id);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") (e.target as HTMLInputElement).blur();
                      else if (e.key === "Escape") {
                        e.stopPropagation();
                        clearNameDraft(iv.id);
                        (e.target as HTMLInputElement).blur();
                      }
                    }}
                    className="w-full truncate rounded border border-transparent bg-transparent px-1 py-0.5 text-sm font-medium text-foreground hover:border-border focus:border-primary focus:bg-background focus:outline-none disabled:cursor-default disabled:hover:border-transparent"
                  />
                  {/* editable range endpoints — each bound to the shared cut, so
                      editing one end moves the adjacent grade's end too, and
                      clampCut keeps every value between its neighbours. */}
                  <span className="flex items-center gap-1.5 whitespace-nowrap font-mono text-[13px] text-muted-foreground">
                    <ClosedDot />
                    {iv.kind === "greaterThanOrEqual" ? (
                      <>
                        {dimension} ≥{" "}
                        {renderCutField(
                          iv.index - 1,
                          `grading-table-min-${iv.index}`,
                          `${gradeName(iv.index)} lower bound`
                        )}
                        {inchFractions ? null : <> {unit}</>}
                      </>
                    ) : iv.kind === "lessThan" ? (
                      <>
                        0 ≤ {dimension} &lt;{" "}
                        {renderCutField(
                          iv.index,
                          `grading-table-max-${iv.index}`,
                          `${gradeName(iv.index)} upper bound`
                        )}
                        {inchFractions ? " " : <> {unit} </>}
                        <OpenDot />
                      </>
                    ) : (
                      <>
                        {renderCutField(
                          iv.index - 1,
                          `grading-table-min-${iv.index}`,
                          `${gradeName(iv.index)} lower bound`
                        )}{" "}
                        ≤ {dimension} &lt;{" "}
                        {renderCutField(
                          iv.index,
                          `grading-table-max-${iv.index}`,
                          `${gradeName(iv.index)} upper bound`
                        )}
                        {inchFractions ? " " : <> {unit} </>}
                        <OpenDot />
                      </>
                    )}
                  </span>
                </div>
              ))}
            </div>

            {/* legend + clear */}
            <div className="flex flex-wrap items-center gap-x-6 gap-y-2 font-mono text-[11px] text-muted-foreground">
              <span className="flex items-center gap-1.5">
                <ClosedDot /> inclusive edge (value belongs here)
              </span>
              <span className="flex items-center gap-1.5">
                <OpenDot /> exclusive edge (belongs to the next grade up)
              </span>
              <span>Drag a pill, or click its number to type an exact value like 2.85.</span>
              <span>Click a grade name (on the bar or in the table) to rename it.</span>
            </div>
            </div>{/* /grading-instrument (dulled until active) */}

            {/* Reset returns to the dulled, inactive default — the grading is then
                excluded from save (turns grading "off"). Shown once active. */}
            {active && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                disabled={disabled}
                onClick={resetToDefault}
                className="text-muted-foreground"
              >
                <RotateCcw className="mr-1 h-4 w-4" /> Reset grades
              </Button>
            )}
          </TooltipProvider>
        )}
      </CollapsibleContent>
    </Collapsible>
  );
}
